import React, { useCallback, useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import Banner from "@/components/Banner";
import Button from "@/components/Button";
import Dropdown from "@/components/Dropdown";
import LicensePhotoModal from "@/components/LicensePhotoModal";
import Loader from "@/components/Loader";
import ProvincePicker from "@/components/ProvincePicker";
import ProvinceSelect from "@/components/ProvinceSelect";
import useLocale from "@/hooks/useLocale";
import { getNextOrCurrentEvent } from "@/utils/events";
import { getUserByEmail } from "@/utils/search";
import {
  getWaiverForm,
  submitWaiver,
  type WaiverFormBooleanValue,
  type WaiverFormStringValue,
} from "@/utils/waiver";

interface WaiverSignatureProps {
  onChange: (data: string) => void;
  error?: string;
}

const WaiverSignature = ({ onChange, error }: WaiverSignatureProps) => {
  const { t } = useTranslation();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [context, setContext] = useState<CanvasRenderingContext2D | null>(null);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.strokeStyle = "#000";
        ctx.lineWidth = 2;
        ctx.lineCap = "round";
        setContext(ctx);
      }
    }
  }, []);

  const clear = () => {
    if (canvasRef.current && context) {
      context.clearRect(
        0,
        0,
        canvasRef.current.width,
        canvasRef.current.height,
      );
      onChange("");
    }
  };

  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    setIsDrawing(true);
    if (context) {
      const rect = canvasRef.current!.getBoundingClientRect();
      context.beginPath();
      context.moveTo(e.clientX - rect.left, e.clientY - rect.top);
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing || !context) return;
    const rect = canvasRef.current!.getBoundingClientRect();
    context.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    context.stroke();
  };

  const stopDrawing = () => {
    if (isDrawing && canvasRef.current) {
      setIsDrawing(false);
      const dataUrl = canvasRef.current.toDataURL("image/jpeg");
      onChange(dataUrl);
    }
  };

  return (
    <div className="mb-10">
      <h2
        className={`font-bold text-center text-blue ${error ? "text-red-600" : ""}`}
      >
        Signature:
      </h2>

      <div
        className={`relative border-[1px] mt-2 ${error ? "border-red-600" : "border-blue"}`}
      >
        <canvas
          ref={canvasRef}
          width={400}
          height={200}
          className="w-full h-[200px] bg-white cursor-crosshair"
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseLeave={stopDrawing}
        />

        <button
          type="button"
          className="font-bold text-[10px] bg-transparent absolute p-0 right-5 bottom-2"
          onClick={clear}
        >
          Clear
        </button>
      </div>

      {error && (
        <p
          className="text-red-600"
          dangerouslySetInnerHTML={{ __html: t(error) }}
        />
      )}
    </div>
  );
};

const Waiver = () => {
  const { t } = useTranslation();
  const locale = useLocale();

  const testDriverInputs = [
    "first_name",
    "last_name",
    "email",
    "phone",
    "driver_license",
    "driver_license_issuer",
    "driver_license_front",
    "address",
    "city",
    "province",
    "zip",
    "vehicule",
    "is_driver",
  ];

  const tutorInputs = [
    "first_name",
    "last_name",
    "address",
    "city",
    "province",
    "zip",
  ];

  const [currentInstanceKey, setCurrentInstanceKey] = useState<string | null>(
    null,
  );
  const [loadingEvent, setLoadingEvent] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchingUser, setSearchingUser] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [timer, setTimer] = useState(30);
  const [isLicenseModalOpen, setIsLicenseModalOpen] = useState(false);
  const [formValues, setFormValues] = useState(getWaiverForm());

  const tutorRequired = tutorInputs.some(
    (input) =>
      formValues.tutor[input as keyof typeof formValues.tutor].value.length > 0,
  );

  const fetchNextEvent = useCallback(
    async (province: string) => {
      if (loadingEvent) return;
      setLoadingEvent(province);

      const nextEvent = await getNextOrCurrentEvent(province);

      if (!nextEvent) {
        setLoadingEvent(null);
        return;
      }

      setCurrentInstanceKey(nextEvent.instance_key);
      setLoadingEvent(null);
    },
    [loadingEvent],
  );

  const handleChangeSignature = useCallback(
    (key: string, signature: string) => {
      setFormValues((prev) => ({
        ...prev,
        [key]: {
          ...prev[key as keyof typeof prev],
          signature: {
            ...prev[key as keyof typeof prev].signature,
            error: "",
            value: signature,
          },
        },
      }));
    },
    [],
  );

  const handleLicensePhotosComplete = useCallback(
    (frontPhoto: Blob, backPhoto: Blob) => {
      setFormValues((prev) => ({
        ...prev,
        test_driver: {
          ...prev.test_driver,
          driver_license_front: {
            ...prev.test_driver.driver_license_front,
            error: "",
            value: frontPhoto as any, // Type assertion to handle File/Blob compatibility
          },
          driver_license_back: {
            ...prev.test_driver.driver_license_back,
            error: "",
            value: backPhoto as any, // Type assertion to handle File/Blob compatibility
          },
        },
      }));
    },
    [],
  );

  const scrollToInputDriverLicenceInput = useCallback(() => {
    const element = document.querySelector(
      `button[name="driver_license_input"]`,
    );
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const handleFetchUser = useCallback(async () => {
    const email = formValues.test_driver.email.value;
    if (!email.trim()) return;
    setSearchingUser(true);

    try {
      const data = await getUserByEmail(email.trim(), currentInstanceKey!);
      if (!data || !data.answers) {
        return;
      }

      const fieldsMap: Record<string, string> = {
        "LAST NAME": "last_name",
        "FIRST NAME": "first_name",
        "STREET ADDRESS": "address",
        CITY: "city",
        STATE: "province",
        ZIP: "zip",
      };

      const updatedFormValues = { ...formValues };
      data.answers.forEach((answer) => {
        const field = fieldsMap[answer.field_key];
        if (field) {
          (
            updatedFormValues.test_driver as unknown as Record<
              string,
              WaiverFormStringValue | WaiverFormBooleanValue
            >
          )[field] = {
            ...(
              updatedFormValues.test_driver as unknown as Record<
                string,
                WaiverFormStringValue | WaiverFormBooleanValue
              >
            )[field],
            value: answer.value,
          };
        }
      });
      setFormValues(updatedFormValues);
    } catch (error) {
      alert((error as Error).message);
    } finally {
      setSearchingUser(false);
    }
  }, [formValues, currentInstanceKey]);

  const handleSubmit = useCallback(async () => {
    setError(false);
    if (submitting) return;

    setSubmitting(true);

    if (!formValues.test_driver.driver_license_front.value) {
      setFormValues((prev) => ({
        ...prev,
        test_driver: {
          ...prev.test_driver,
          driver_license_front: {
            ...prev.test_driver.driver_license_front,
            error: "waiver.licenseError",
          },
        },
      }));
      setSubmitting(false);
      scrollToInputDriverLicenceInput();
      return;
    }

    if (!formValues.test_driver.driver_license_back.value) {
      setFormValues((prev) => ({
        ...prev,
        test_driver: {
          ...prev.test_driver,
          driver_license_back: {
            ...prev.test_driver.driver_license_back,
            error: "waiver.licenseError",
          },
        },
      }));
      setSubmitting(false);
      scrollToInputDriverLicenceInput();
      return;
    }

    if (!formValues.test_driver.signature.value) {
      setFormValues((prev) => ({
        ...prev,
        test_driver: {
          ...prev.test_driver,
          signature: {
            ...prev.test_driver.signature,
            error: "waiver.signatureError",
          },
        },
      }));
      setSubmitting(false);
      return;
    }

    if (tutorRequired && !formValues.tutor.signature.value) {
      setFormValues((prev) => ({
        ...prev,
        tutor: {
          ...prev.tutor,
          signature: {
            ...prev.tutor.signature,
            error: "waiver.signatureError",
          },
        },
      }));
      setSubmitting(false);
      return;
    }

    try {
      await submitWaiver(
        currentInstanceKey!,
        formValues.test_driver,
        tutorRequired ? formValues.tutor : undefined,
        locale,
      );

      setSuccess(true);
      window.scrollTo({ top: 0, behavior: "instant" });

      intervalRef.current = setInterval(() => {
        setTimer((prev) => {
          if (prev === 0) return 0;
          const newTimer = prev - 1;
          if (newTimer === 0) {
            handleReset();
          }
          return newTimer;
        });
      }, 1000);
    } catch (_error) {
      setError(true);
    } finally {
      setSubmitting(false);
    }
  }, [
    submitting,
    formValues,
    tutorRequired,
    currentInstanceKey,
    locale,
    scrollToInputDriverLicenceInput,
  ]);

  const handleReset = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setFormValues(getWaiverForm());
    setSuccess(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const testDriverFullName =
    !formValues.test_driver.last_name.value &&
    !formValues.test_driver.first_name.value
      ? "_________________________"
      : `<u>&nbsp;${formValues.test_driver.first_name.value} ${formValues.test_driver.last_name.value}&nbsp;</u>`;

  const onCloseLicenseModal = useCallback(() => {
    setIsLicenseModalOpen(false);
  }, []);

  const handleInputChange = useCallback(
    (
      section: "test_driver" | "tutor",
      field: string,
      value: string | boolean,
    ) => {
      setFormValues((prev) => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: {
            ...prev[section][field as keyof (typeof prev)[typeof section]],
            value,
          },
        },
      }));
    },
    [],
  );

  if (success) {
    return (
      <main>
        <Banner />
        <div className="min-h-screen">
          <h1 className="text-2xl font-bold mt-20 uppercase text-center">
            {t("waiver.success")}
          </h1>

          <div className="flex justify-center mt-20">
            <Button onClick={handleReset}>
              {t("waiver.reset", { seconds: timer })}
            </Button>
          </div>
        </div>
      </main>
    );
  }

  if (!currentInstanceKey) {
    return (
      <main>
        <Banner />
        <ProvincePicker
          setProvince={fetchNextEvent}
          loadingEvent={loadingEvent}
          notFound={false}
        />
      </main>
    );
  }

  return (
    <main>
      <Banner />

      <form
        className="px-10"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <h1 className="text-2xl font-bold mt-10 uppercase text-center">
          Ford Motor Company of Canada
        </h1>
        <h2 className="text-2xl font-normal uppercase mb-16 text-center">
          {t("waiver.title")}
        </h2>

        <div className="flex flex-wrap justify-between">
          {testDriverInputs.map((input) => (
            <div
              key={input}
              className={`w-[100%] relative ${
                !formValues.test_driver[
                  input as keyof typeof formValues.test_driver
                ].is_full
                  ? "md:w-[49%]"
                  : ""
              }`}
            >
              {input === "province" || input === "driver_license_issuer" ? (
                <ProvinceSelect
                  name={input}
                  required={
                    formValues.test_driver[
                      input as keyof typeof formValues.test_driver
                    ].required
                  }
                  value={
                    formValues.test_driver[
                      input as keyof typeof formValues.test_driver
                    ].value as string
                  }
                  placeholder={`${t(`signup.${input}`)}${
                    formValues.test_driver[
                      input as keyof typeof formValues.test_driver
                    ].required
                      ? "*"
                      : ""
                  }`}
                  autocomplete={input === "province"}
                  onChange={(value) =>
                    handleInputChange("test_driver", input, value)
                  }
                />
              ) : input === "driver_license_front" ? (
                <div className="my-2">
                  <Button
                    name="driver_license_input"
                    className="w-[100%] relative"
                    onClick={() => setIsLicenseModalOpen(true)}
                  >
                    {!!formValues.test_driver.driver_license_front.value &&
                    !!formValues.test_driver.driver_license_back.value
                      ? t("waiver.licenseModal.done")
                      : t("waiver.licenseModal.open")}
                    <img
                      src="/icons/photo.svg"
                      alt="camera"
                      className="block absolute right-5"
                    />
                  </Button>
                  {(!!formValues.test_driver.driver_license_front.error ||
                    !!formValues.test_driver.driver_license_back.error) && (
                    <p
                      className="text-red-600"
                      dangerouslySetInnerHTML={{
                        __html: t("waiver.licenseError"),
                      }}
                    />
                  )}
                </div>
              ) : input === "is_driver" ? (
                <label className="checkbox flex mt-7 text-dark-blue text-xs items-center">
                  <input
                    type="checkbox"
                    name="is_driver"
                    checked={formValues.test_driver.is_driver.value}
                    onChange={(e) =>
                      handleInputChange("test_driver", input, e.target.checked)
                    }
                  />
                  {t(`signup.${input}`)}
                </label>
              ) : input === "vehicule" &&
                formValues.test_driver.vehicule.options.length ? (
                <Dropdown
                  name={input}
                  options={formValues.test_driver.vehicule.options}
                  value={formValues.test_driver.vehicule.value as string}
                  required={formValues.test_driver.vehicule.required}
                  placeholder={`${t(`signup.${input}`)}${formValues.test_driver.vehicule.required ? "*" : ""}`}
                  onChange={(value) =>
                    handleInputChange("test_driver", input, value)
                  }
                />
              ) : (
                <div className="relative">
                  <input
                    type="text"
                    name={input}
                    className={`w-[100%] mt-2 placeholder:text-blue text-blue bg-gray-200 rounded-full font-semibold py-4 px-7 mb-4 ${input === "email" ? "mb-0 pr-[50px]" : ""}`}
                    placeholder={`${t(`signup.${input}`)}${
                      formValues.test_driver[
                        input as keyof typeof formValues.test_driver
                      ].required
                        ? "*"
                        : ""
                    }`}
                    required={
                      formValues.test_driver[
                        input as keyof typeof formValues.test_driver
                      ].required
                    }
                    value={
                      formValues.test_driver[
                        input as keyof typeof formValues.test_driver
                      ].value as string
                    }
                    onChange={(e) =>
                      handleInputChange("test_driver", input, e.target.value)
                    }
                  />

                  {input === "email" && (
                    <button
                      type="button"
                      className="bg-dark-blue rounded-[50%] flex justify-center items-center absolute right-[12px] top-[20px] w-[30px] h-[30px] pr-0 pl-0 p-0 py-0 min-w-[20px] mt-0"
                      onClick={handleFetchUser}
                    >
                      {!searchingUser ? (
                        <img
                          className="min-w-[20px] h-[20px] mt-0 mr-0 m-0"
                          src="/icons/planet.svg"
                          alt=""
                        />
                      ) : (
                        <Loader />
                      )}
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>

        <p
          className="font-extralight mt-10 mb-10"
          dangerouslySetInnerHTML={{
            __html: t("waiver.text_1", {
              name: testDriverFullName,
            }),
          }}
        />

        <div
          className="max-h-[550px] overflow-y-auto p-4 bg-gray-200 long-text"
          dangerouslySetInnerHTML={{ __html: t("waiver.text") }}
        />

        <label className="checkbox flex mt-7 text-dark-blue text-xs">
          <input
            type="checkbox"
            name="marketing_opt_in"
            checked={formValues.test_driver.marketing_opt_in.value}
            onChange={(e) =>
              handleInputChange(
                "test_driver",
                "marketing_opt_in",
                e.target.checked,
              )
            }
          />
          {t("waiver.optIn")}
        </label>

        <div
          className="font-bold text-[12px] mt-10"
          dangerouslySetInnerHTML={{ __html: t("waiver.termsText") }}
        />

        <div className="flex flex-wrap justify-between mt-10">
          <input
            type="text"
            name="driver_full_name"
            value={`${formValues.test_driver.first_name.value} ${formValues.test_driver.last_name.value}`}
            className="w-[160%] md:w-[59%] mt-2 placeholder:text-blue text-blue bg-gray-200 rounded-full font-semibold py-4 px-7 mb-4"
            placeholder="Full Name"
            disabled
          />
          <input
            type="date"
            name="date"
            placeholder="Date"
            className="w-[100%] md:w-[39%] mt-2 placeholder:text-blue text-blue bg-gray-200 rounded-full font-semibold py-4 px-7 mb-4"
            required
            value={formValues.test_driver.date.value}
            onChange={(e) =>
              handleInputChange("test_driver", "date", e.target.value)
            }
          />
        </div>

        <div className="mt-10">
          <WaiverSignature
            onChange={(data) => handleChangeSignature("test_driver", data)}
            error={formValues.test_driver.signature.error}
          />
        </div>

        <div className="flex justify-center mb-24">
          <Button type="submit" loading={submitting}>
            {t("waiver.submit")}
          </Button>
        </div>

        {error && (
          <div className="flex justify-center mt-5">
            <p className="text-red-600 font-bold">{t("error")}</p>
          </div>
        )}

        <h4 className="text-blue font-bold">{t("waiver.tutor_title")}</h4>
        <p
          dangerouslySetInnerHTML={{
            __html: t("waiver.tutor_description", {
              name: testDriverFullName,
            }),
          }}
        />

        <div className="flex flex-wrap justify-between mt-10 mb-10">
          {tutorInputs.map((input) => (
            <div key={input} className="w-[100%] md:w-[49%]">
              {input === "province" ? (
                <ProvinceSelect
                  name={input}
                  required={
                    formValues.tutor[input as keyof typeof formValues.tutor]
                      .required
                  }
                  value={
                    formValues.tutor[input as keyof typeof formValues.tutor]
                      .value
                  }
                  placeholder={`${t(`signup.${input}`)}${
                    formValues.tutor[input as keyof typeof formValues.tutor]
                      .required
                      ? "*"
                      : ""
                  }`}
                  autocomplete={false}
                  onChange={(value) => {
                    console.log("ProvinceSelect onChange value", value);
                    handleInputChange("tutor", input, value);
                  }}
                />
              ) : (
                <input
                  type="text"
                  autoComplete="off"
                  name={`no_autocomplete_${input}`}
                  className="w-[100%] mt-2 placeholder:text-blue text-blue bg-gray-200 rounded-full font-semibold py-4 px-7 mb-4"
                  placeholder={t(`signup.${input}`)}
                  required={tutorRequired}
                  readOnly
                  value={
                    formValues.tutor[input as keyof typeof formValues.tutor]
                      .value
                  }
                  onChange={(e) =>
                    handleInputChange("tutor", input, e.target.value)
                  }
                  onFocus={(event) => event.target.removeAttribute("readonly")}
                  onBlur={(event) =>
                    event.target.setAttribute("readonly", "true")
                  }
                />
              )}
            </div>
          ))}
        </div>

        <WaiverSignature
          onChange={(data) => handleChangeSignature("tutor", data)}
          error={formValues.tutor.signature.error}
        />

        <p className="text-gray-400 mb-32">{t("waiver.admin_warning")}</p>

        <p>
          <a
            href={
              locale === "fr"
                ? "https://fr.ford.ca/help/privacy/"
                : "https://www.ford.ca/help/privacy/"
            }
          >
            {t("waiver.privacy_policy")}
          </a>
        </p>
      </form>

      <LicensePhotoModal
        isOpen={isLicenseModalOpen}
        onClose={onCloseLicenseModal}
        onComplete={handleLicensePhotosComplete}
      />
    </main>
  );
};

export default Waiver;
